import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { onboardingAPI, storesAPI } from '../../api';

/**
 * OnboardingChecklist — appears on the dashboard until the store is fully set up.
 *
 * Features:
 * - "Try with sample data" button for brand-new empty stores
 * - First incomplete step highlighted with pulse animation
 * - Derives state from store counters (instant) then confirms via API
 */

const STEPS = [
  {
    id: 'add_product',
    label: 'Add your first product',
    description: 'Build your product catalogue',
    path: '/products/new',
    icon: '📦',
    actionLabel: 'Add product',
  },
  {
    id: 'create_sale',
    label: 'Record your first sale',
    description: 'Start tracking your revenue',
    path: '/sales/new',
    icon: '🛍️',
    actionLabel: 'Record sale',
  },
  {
    id: 'invite_staff',
    label: 'Invite a team member',
    description: 'Add your staff so they can log in',
    path: '/staff',
    icon: '👥',
    actionLabel: 'Invite staff',
  },
  {
    id: 'view_dashboard',
    label: 'Explore your dashboard',
    description: 'See your live stats and insights',
    path: '/dashboard',
    icon: '📊',
    actionLabel: 'Mark done',
    // This step requires explicit user action — it does NOT auto-complete.
    manualComplete: true,
  },
];

const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { selectedStore, selectStore } = useStore();

  const [stepsDone, setStepsDone] = useState(() => ({
    add_product:    (selectedStore?.products_count ?? 0) > 0,
    create_sale:    (selectedStore?.sales_count ?? 0) > 0,
    invite_staff:   (selectedStore?.staff_count ?? 0) > 0,
    // view_dashboard is NOT auto-completed — user must click "Mark done"
    view_dashboard: false,
  }));
  const [seeding, setSeeding] = useState(false);
  const [seedDone, setSeedDone] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);

  const deriveFromStore = useCallback((store) => ({
    add_product:    (store?.products_count ?? 0) > 0,
    create_sale:    (store?.sales_count ?? 0) > 0,
    invite_staff:   (store?.staff_count ?? 0) > 0,
    // Preserve existing view_dashboard state — don't auto-reset it
    view_dashboard: false,
  }), []);

  // Hydrate from API after initial render
  useEffect(() => {
    if (!selectedStore?.id) return;
    const fromStore = deriveFromStore(selectedStore);
    setStepsDone(prev => ({ ...fromStore, view_dashboard: prev.view_dashboard }));

    onboardingAPI.getStatus(selectedStore.id)
      .then(res => {
        const apiDone = {};
        for (const step of res.data.steps) {
          apiDone[step.step_key] = step.completed;
        }
        setStepsDone(prev => {
          const merged = { ...prev };
          for (const key of Object.keys(apiDone)) {
            // Always trust the API for view_dashboard (persists across sessions)
            merged[key] = merged[key] || apiDone[key];
          }
          return merged;
        });
      })
      .catch(() => {});
  }, [selectedStore, deriveFromStore]);

  const handleMarkDone = async (stepId) => {
    if (!selectedStore?.id || markingDone) return;
    setMarkingDone(true);
    try {
      await onboardingAPI.completeStep(selectedStore.id, stepId);
      setStepsDone(prev => ({ ...prev, [stepId]: true }));
    } catch {
      // Optimistically mark done anyway — UX > strict API sync
      setStepsDone(prev => ({ ...prev, [stepId]: true }));
    } finally {
      setMarkingDone(false);
    }
  };

  const handleSeedDemo = async () => {
    if (!selectedStore?.id || seeding) return;
    setSeeding(true);
    try {
      await onboardingAPI.seedDemo(selectedStore.id);
      // Refresh store to get updated counters
      const res = await storesAPI.get(selectedStore.id);
      selectStore(res.data);
      setSeedDone(true);
    } catch {
      // silently fail — store may already have data
    } finally {
      setSeeding(false);
    }
  };

  const completedCount = STEPS.filter(s => stepsDone[s.id]).length;
  const allDone = completedCount === STEPS.length;
  const pct = Math.round((completedCount / STEPS.length) * 100);

  // First incomplete step (to highlight)
  const firstIncompleteId = STEPS.find(s => !stepsDone[s.id])?.id;

  if (allDone || selectedStore?.is_onboarded) return null;

  // Is this a completely empty store?
  const isEmpty = (selectedStore?.products_count ?? 0) === 0
    && (selectedStore?.sales_count ?? 0) === 0
    && !seedDone;

  return (
    <div className="onboarding-card">
      <div className="onboarding-card__header">
        <div>
          <h2 className="onboarding-card__title">Get started with AivioraOps</h2>
          <p className="onboarding-card__subtitle">
            Complete these steps to set up your store.
          </p>
        </div>
        <div className="onboarding-card__score">
          <span className="onboarding-card__pct">{pct}%</span>
          <span className="onboarding-card__pct-label">complete</span>
        </div>
      </div>

      <div className="onboarding-progress">
        <div className="onboarding-progress__bar">
          <div
            className="onboarding-progress__fill"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Sample data offer — only for completely empty stores */}
      {isEmpty && (
        <div className="onboarding-seed-card">
          <div className="onboarding-seed-card__left">
            <span className="onboarding-seed-card__icon">🚀</span>
            <div>
              <p className="onboarding-seed-card__title">See AivioraOps in action</p>
              <p className="onboarding-seed-card__desc">
                Load 3 products + 30 days of sales data so your dashboard shows real insights instantly.
              </p>
            </div>
          </div>
          <button
            className="onboarding-seed-card__btn"
            onClick={handleSeedDemo}
            disabled={seeding}
          >
            {seeding ? 'Loading…' : 'Try with sample data'}
          </button>
        </div>
      )}

      <div className="onboarding-steps">
        {STEPS.map((step, idx) => {
          const done = stepsDone[step.id];
          const isCurrent = step.id === firstIncompleteId;
          // A step is locked if a prior step isn't complete yet
          const isLocked = !done && idx > 0 && !stepsDone[STEPS[idx - 1].id];

          return (
            <div
              key={step.id}
              className={[
                'onboarding-step',
                done ? 'onboarding-step--done' : '',
                isCurrent ? 'onboarding-step--current' : '',
                isLocked ? 'onboarding-step--locked' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className="onboarding-step__icon-wrap">
                {done
                  ? <span className="onboarding-step__check">✓</span>
                  : <span className="onboarding-step__icon">{step.icon}</span>
                }
              </span>
              <div className="onboarding-step__body">
                <span className="onboarding-step__label">{step.label}</span>
                <span className="onboarding-step__desc">{step.description}</span>
              </div>
              {!done && !isLocked && (
                step.manualComplete ? (
                  <button
                    className="onboarding-step__action-btn onboarding-step__action-btn--mark"
                    onClick={() => handleMarkDone(step.id)}
                    disabled={markingDone}
                  >
                    {markingDone ? '…' : step.actionLabel}
                  </button>
                ) : (
                  <button
                    className="onboarding-step__action-btn"
                    onClick={() => navigate(step.path)}
                  >
                    {step.actionLabel}
                  </button>
                )
              )}
              {done && isCurrent === false && isLocked === false && idx < STEPS.length - 1 && null}
            </div>
          );
        })}
      </div>

      <div className="onboarding-card__footer">
        <span className="onboarding-card__count">
          {completedCount}/{STEPS.length} steps complete
        </span>
      </div>
    </div>
  );
};

export default OnboardingChecklist;
